import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';

type Theme = 'Light' | 'Dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme = 'Light' }) => {
  // Use ref to track if we're mounting to prevent flash
  const isMountingRef = useRef(true);
  
  // Initialize theme from localStorage or initialTheme
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    const initialValue = savedTheme || initialTheme;
    
    // Apply theme immediately on mount to prevent flash
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      if (initialValue === 'Dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
    
    return initialValue;
  });

  // Apply theme class to document root and save to localStorage
  useEffect(() => {
    // Skip the first render since we already applied theme in useState
    if (isMountingRef.current) {
      isMountingRef.current = false;
      return;
    }
    
    const root = document.documentElement;
    
    // Prevent unnecessary DOM updates
    const isDark = theme === 'Dark';
    const hasDarkClass = root.classList.contains('dark');
    
    if (isDark && !hasDarkClass) {
      root.classList.add('dark');
    } else if (!isDark && hasDarkClass) {
      root.classList.remove('dark');
    }
    
    // Save to localStorage only if changed
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme !== theme) {
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prevTheme => prevTheme === 'Light' ? 'Dark' : 'Light');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};
